'use client';

import type { FC } from 'react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  SupportUserAttachment,
  SupportUserMessage,
} from '../../services/generated/Api';
import { SupportUserMessageKindEnum } from '../../services/generated/Api';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { supportAttachmentUrl } from '../../queries/useSupportQueries';
import { getPortalRoot } from '../portalRoot';
import { FaDownload, FiX } from '../icons';
import { cn } from '../../utils/cn';
import translations from './SupportChat.translations';

type KindLabelKey =
  | 'kindText'
  | 'kindPhoto'
  | 'kindVideo'
  | 'kindVideoNote'
  | 'kindVoice'
  | 'kindAudio'
  | 'kindDocument'
  | 'kindSticker'
  | 'kindAnimation'
  | 'kindContact'
  | 'kindLocation'
  | 'kindPoll'
  | 'kindOther';

const KIND_LABEL_KEY: Record<SupportUserMessageKindEnum, KindLabelKey> = {
  [SupportUserMessageKindEnum.Text]: 'kindText',
  [SupportUserMessageKindEnum.Photo]: 'kindPhoto',
  [SupportUserMessageKindEnum.Video]: 'kindVideo',
  [SupportUserMessageKindEnum.VideoNote]: 'kindVideoNote',
  [SupportUserMessageKindEnum.Voice]: 'kindVoice',
  [SupportUserMessageKindEnum.Audio]: 'kindAudio',
  [SupportUserMessageKindEnum.Document]: 'kindDocument',
  [SupportUserMessageKindEnum.Sticker]: 'kindSticker',
  [SupportUserMessageKindEnum.Animation]: 'kindAnimation',
  [SupportUserMessageKindEnum.Contact]: 'kindContact',
  [SupportUserMessageKindEnum.Location]: 'kindLocation',
  [SupportUserMessageKindEnum.Poll]: 'kindPoll',
  [SupportUserMessageKindEnum.Other]: 'kindOther',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

function payloadOf(message: SupportUserMessage): Record<string, unknown> | null {
  return isRecord(message.payload) ? message.payload : null;
}

const Plaque: FC<{ title: string; note?: string; href?: string }> = ({ title, note, href }) => {
  const body = (
    <div className="flex items-center gap-2 rounded-xl border border-separator-non-opaque bg-black/20 px-3 py-2.5">
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-[13px] font-medium text-label-primary">{title}</span>
        {note != null && <span className="text-[11px] text-label-tertiary">{note}</span>}
      </div>
      {href != null && <FaDownload size={14} className="ml-auto shrink-0 text-label-tertiary" />}
    </div>
  );

  if (href == null) return body;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="block active:opacity-70">
      {body}
    </a>
  );
};

const Lightbox: FC<{ url: string; alt: string; onClose: () => void }> = ({ url, alt, onClose }) =>
  createPortal(
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={alt}
        className="absolute end-4 top-[max(16px,var(--tg-viewport-safe-area-inset-top,0px))] flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white"
      >
        <FiX size={18} />
      </button>
      <img src={url} alt={alt} className="max-h-full max-w-full rounded-xl object-contain" />
    </div>,
    getPortalRoot(),
  );

const PhotoView: FC<{ attachment: SupportUserAttachment; label: string }> = ({ attachment, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const { t } = useLocalTranslation(translations);
  const url = supportAttachmentUrl(attachment.id);

  if (hasFailed) {
    return <Plaque title={label} note={t('fileUnavailable')} href={url} />;
  }

  return (
    <>
      <img
        src={url}
        alt={label}
        loading="lazy"
        onError={() => setHasFailed(true)}
        onClick={() => setIsOpen(true)}
        style={
          attachment.width != null && attachment.height != null
            ? { aspectRatio: `${attachment.width} / ${attachment.height}` }
            : undefined
        }
        className="max-h-[320px] w-full max-w-[260px] cursor-zoom-in rounded-xl object-cover"
      />
      {isOpen && <Lightbox url={url} alt={label} onClose={() => setIsOpen(false)} />}
    </>
  );
};

const StickerView: FC<{ attachment: SupportUserAttachment; animated: boolean }> = ({
  attachment,
  animated,
}) => {
  const { t } = useLocalTranslation(translations);
  const [hasFailed, setHasFailed] = useState(false);

  if (animated || hasFailed) {
    return <Plaque title={t('sticker')} />;
  }

  return (
    <img
      src={supportAttachmentUrl(attachment.id)}
      alt={t('sticker')}
      loading="lazy"
      onError={() => setHasFailed(true)}
      className="h-32 w-32 object-contain"
    />
  );
};

const VideoView: FC<{
  attachment: SupportUserAttachment;
  label: string;
  round?: boolean;
  looping?: boolean;
}> = ({ attachment, label, round, looping }) => {
  const { t } = useLocalTranslation(translations);
  const [hasFailed, setHasFailed] = useState(false);
  const url = supportAttachmentUrl(attachment.id);

  if (hasFailed) {
    return <Plaque title={label} note={t('fileUnavailable')} href={url} />;
  }

  return (
    <video
      src={url}
      controls={!looping}
      autoPlay={looping}
      loop={looping}
      muted={looping}
      playsInline
      preload="metadata"
      onError={() => setHasFailed(true)}
      className={cn(
        'max-h-[320px] w-full max-w-[260px] object-cover',
        round ? 'aspect-square rounded-full' : 'rounded-xl',
      )}
    />
  );
};

const SoundView: FC<{ attachment: SupportUserAttachment; label: string }> = ({ attachment, label }) => {
  const { t } = useLocalTranslation(translations);
  const [hasFailed, setHasFailed] = useState(false);
  const url = supportAttachmentUrl(attachment.id);

  if (hasFailed) {
    return <Plaque title={label} note={t('fileUnavailable')} href={url} />;
  }

  return (
    <div className="flex w-full max-w-[260px] items-center gap-2">
      <audio
        src={url}
        controls
        preload="metadata"
        onError={() => setHasFailed(true)}
        className="h-10 min-w-0 flex-1"
      />
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('download')}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-label-tertiary active:opacity-70"
      >
        <FaDownload size={16} />
      </a>
    </div>
  );
};

const DocumentView: FC<{ attachment: SupportUserAttachment; label: string }> = ({
  attachment,
  label,
}) => (
  <Plaque
    title={attachment.fileName || label}
    note={attachment.fileSize != null ? formatFileSize(attachment.fileSize) : undefined}
    href={supportAttachmentUrl(attachment.id)}
  />
);

const ContactCard: FC<{ message: SupportUserMessage }> = ({ message }) => {
  const { t } = useLocalTranslation(translations);
  const payload = payloadOf(message);
  const name = typeof payload?.name === 'string' ? payload.name : t('contact');
  const phone = typeof payload?.phoneNumber === 'string' ? payload.phoneNumber : t('contactNoPhone');
  return <Plaque title={name} note={phone} />;
};

const LocationCard: FC<{ message: SupportUserMessage }> = ({ message }) => {
  const { t } = useLocalTranslation(translations);
  const payload = payloadOf(message);
  const latitude = typeof payload?.latitude === 'number' ? payload.latitude : null;
  const longitude = typeof payload?.longitude === 'number' ? payload.longitude : null;

  if (latitude == null || longitude == null) return <Plaque title={t('location')} />;

  return (
    <a
      href={`https://maps.google.com/?q=${latitude},${longitude}`}
      target="_blank"
      rel="noopener noreferrer"
      className="block active:opacity-70"
    >
      <Plaque title={t('location')} note={`${latitude.toFixed(5)}, ${longitude.toFixed(5)}`} />
    </a>
  );
};

const PollCard: FC<{ message: SupportUserMessage }> = ({ message }) => {
  const { t } = useLocalTranslation(translations);
  const payload = payloadOf(message);
  const question = typeof payload?.question === 'string' ? payload.question : t('poll');
  return <Plaque title={question} note={t('poll')} />;
};

export const SupportAttachment: FC<{ message: SupportUserMessage }> = ({ message }) => {
  const { t } = useLocalTranslation(translations);
  const attachment = message.attachments[0];
  const label = t(KIND_LABEL_KEY[message.kind]);

  if (!attachment) {
    switch (message.kind) {
      case SupportUserMessageKindEnum.Contact:
        return <ContactCard message={message} />;
      case SupportUserMessageKindEnum.Location:
        return <LocationCard message={message} />;
      case SupportUserMessageKindEnum.Poll:
        return <PollCard message={message} />;
      default:
        return <Plaque title={t('unsupported', { kind: label })} />;
    }
  }

  switch (message.kind) {
    case SupportUserMessageKindEnum.Photo:
      return <PhotoView attachment={attachment} label={label} />;
    case SupportUserMessageKindEnum.Sticker:
      return <StickerView attachment={attachment} animated={payloadOf(message)?.animated === true} />;
    case SupportUserMessageKindEnum.Video:
      return <VideoView attachment={attachment} label={label} />;
    case SupportUserMessageKindEnum.Animation:
      return <VideoView attachment={attachment} label={label} looping />;
    case SupportUserMessageKindEnum.VideoNote:
      return <VideoView attachment={attachment} label={label} round />;
    case SupportUserMessageKindEnum.Voice:
    case SupportUserMessageKindEnum.Audio:
      return <SoundView attachment={attachment} label={label} />;
    default:
      return <DocumentView attachment={attachment} label={label} />;
  }
};
